import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import {
  BeakerIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import { GET_RAW_MATERIALS } from '../../graphql/costing-queries'
import { DELETE_RAW_MATERIAL } from '../../graphql/costing-mutations'
import { RawMaterial } from '../../types/costing'
import { formatCostPerUnit } from '../../lib/costingUtils'
import LoadingSpinner from '../../components/LoadingSpinner'
import toast from 'react-hot-toast'
import { usePermissions } from '../../hooks/usePermissions'
import { PERMISSIONS } from '../../lib/permissions'

const RawMaterialsPage = () => {
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const canManage = hasPermission(PERMISSIONS.COSTING_MANAGE)

  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(undefined)

  const { data, loading, error, refetch } = useQuery(GET_RAW_MATERIALS, {
    variables: {
      filters: {
        active: activeFilter,
        search: search || undefined,
      },
    },
  })

  const [deleteRawMaterial] = useMutation(DELETE_RAW_MATERIAL, {
    onCompleted: () => {
      toast.success('Raw material deleted')
      refetch()
    },
    onError: (err) => {
      toast.error(`Cannot delete: ${err.message}`)
    },
  })

  const handleDelete = (material: RawMaterial) => {
    if (!window.confirm(`Delete "${material.name}"? This cannot be undone.`)) return
    deleteRawMaterial({ variables: { id: material.id } })
  }

  const materials: RawMaterial[] = data?.rawMaterials ?? []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Raw Materials</h1>
          <p className="mt-1 text-sm text-gray-500">
            Ingredients and their costs used for COGS tracking.
          </p>
        </div>
        {canManage && (
          <Link
            to="/costing/raw-materials/new"
            className="inline-flex items-center gap-x-2 rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500"
          >
            <PlusIcon className="h-5 w-5" />
            New Raw Material
          </Link>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search materials..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-9 w-full"
          />
        </div>
        <select
          value={activeFilter === undefined ? 'all' : activeFilter ? 'active' : 'inactive'}
          onChange={(e) => {
            if (e.target.value === 'all') setActiveFilter(undefined)
            else setActiveFilter(e.target.value === 'active')
          }}
          className="input w-40"
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <div className="text-center py-12 text-red-600">{error.message}</div>
      ) : materials.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-12 text-center">
          <BeakerIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-sm font-semibold text-gray-900">No raw materials found</h3>
          <p className="mt-2 text-sm text-gray-500">
            {search ? 'Try a different search term.' : 'Get started by adding your first ingredient.'}
          </p>
          {canManage && !search && (
            <Link
              to="/costing/raw-materials/new"
              className="mt-6 inline-flex items-center gap-x-2 rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500"
            >
              <PlusIcon className="h-5 w-5" />
              New Raw Material
            </Link>
          )}
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Unit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current Cost
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {materials.map((m) => (
                <tr
                  key={m.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/costing/raw-materials/${m.id}`)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <BeakerIcon className="h-4 w-4 text-gray-400 shrink-0" />
                      <span className="text-sm font-medium text-gray-900">{m.name}</span>
                    </div>
                    {m.notes && (
                      <p className="mt-0.5 text-xs text-gray-500 truncate max-w-xs">{m.notes}</p>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                      {m.unit}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {formatCostPerUnit(m.current_cost_per_unit_millicents, m.unit)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {m.active ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircleIcon className="h-3.5 w-3.5" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        <XCircleIcon className="h-3.5 w-3.5" />
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    {canManage && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDelete(m)
                        }}
                        className="text-gray-400 hover:text-red-600 transition-colors p-1 rounded"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default RawMaterialsPage
